import Box from '@mui/material/Box';
import { useDocuments } from '../../context/DocumentsProvider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState, useMemo } from 'react';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsTwoToneIcon from '@mui/icons-material/SmsTwoTone';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { Typography, Container, LinearProgress, Paper } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DocumentFilterButton from '../documents/DocumentFilterButton';
import TablePagination from '@mui/material/TablePagination';
import { useNav } from "../../context/NavProvider";
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';

function DocumentsPage() {
    const { setShowFooter } = useNav();
    setShowFooter(false);
    const { useGetDocuments } = useDocuments();
    const { documents, isDocumentsLoading } = useGetDocuments();
    const [data, setData] = useState([])
    const [filteredData, setFilteredData] = useState([])
    const [filter, setFilter] = useState(null);
    const [selected, setSelected] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [width, setWidth] = useState(window.innerWidth);
    const [expandedRow, setExpandedRow] = useState(false);

    function applyFilter(filter) {
        setFilter(filter);
        setSelected(filter)
    };

    useEffect(() => {
        if (!isDocumentsLoading) {
            setData(documents?.documents)
            setFilteredData(documents?.documents)
        }
    }
        , [isDocumentsLoading])

    useEffect(() => {
        if (!(filter === null)) {
            setFilteredData(data.filter(row => row.documentType === filter))
        }
        if (filter === "All") {
            setFilteredData(data)
        }
    }
        , [filter])

    const paginatedDocuments = useMemo(() => {
        return filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [filteredData, page, rowsPerPage]);

    const openInNewTab = (url) => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
        if (newWindow) newWindow.opener = null
    }

    const onClickUrl = (url) => {
        return () => openInNewTab(url)
    }

    useEffect(() => {
        const handleResizeWindow = () => setWidth(window.innerWidth);
        // subscribe to window resize event "onComponentDidMount"
        window.addEventListener("resize", handleResizeWindow);
        return () => {
            // unsubscribe "onComponentDestroy"
            window.removeEventListener("resize", handleResizeWindow);
        };
    }, []);

    if (
        isDocumentsLoading
    ) {
        return <LinearProgress />;
    }

    return (
        !isDocumentsLoading &&
            data.length === 0 ? (<>
                <Container sx={{ mt: 4, mb: 7, paddingBottom: "350px" }}>
                    <Typography fontSize={34} fontWeight={"bold"} sx={{ mb: -4 }}>
                        My Documents
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <Typography fontSize={14} sx={{ mt: 7 }}>
                            There are currently no documents available for you to view. Please check back later, as new documents will appear here as they become available.
                        </Typography>
                    </Box>
                </Container>
                {setShowFooter(true)}</>) :
            (<>
                <Container sx={{ mt: 4, mb: 7, paddingBottom: "350px" }}>
                    <Typography fontSize={34} fontWeight={"bold"} sx={{ mb: -4 }}>
                        My Documents
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <Typography fontSize={14} sx={{ mt: 7 }}>
                            Easily access and review all your important documents with us, including letters, agreements & communications. You'll also have the option to download them for your records, helping you stay organised and in control of your account at any time.
                        </Typography>
                    </Box>
                    <Box sx={{ mb: 3, width: { xs: "100%", md: "50%" } }}>
                        <DocumentFilterButton title="All" id="all-filter-btn" applyFilter={applyFilter} isSelected={selected}></DocumentFilterButton>
                        <DocumentFilterButton title="Letter" id="letter-filter-btn" applyFilter={applyFilter} isSelected={selected}></DocumentFilterButton>
                        <DocumentFilterButton title="SMS" id="sms-filter-btn" applyFilter={applyFilter} isSelected={selected}></DocumentFilterButton>
                        <DocumentFilterButton title="Email" id="email-filter-btn" applyFilter={applyFilter} isSelected={selected}></DocumentFilterButton>
                    </Box>
                    <Table sx={{ border: "transparent", borderCollapse: "separate", borderSpacing: "0px 0px" }}>
                        <TableBody>
                            {paginatedDocuments.map((row, index) => (<>
                                <TableRow sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)" }}>
                                    <TableCell align="center" >{row.documentType === "Letter" ? <MailOutlineIcon fontSize='large' sx={{ color: "primary.dark" }}></MailOutlineIcon> : row.documentType === "SMS" ? <SmsTwoToneIcon fontSize='large' sx={{ color: "primary.dark" }}></SmsTwoToneIcon> : row.documentType === "Email" ? <AlternateEmailIcon fontSize='large' sx={{ color: "primary.dark" }}></AlternateEmailIcon> : null}</TableCell>
                                    <TableCell>{width > 452 ? (<><Typography fontSize={20} sx={{ color: "primary.main" }}> <b>{row.documentType}</b></Typography> <Typography><b>Subject:</b> {row.documentSubtype}</Typography></>) : (<><Typography fontSize={20} sx={{ color: "primary.main" }}> {row.documentType}</Typography> <Typography><b>Subject:</b> {row.documentSubtype}</Typography><Typography><b>Sent:</b> {new Date(Date.parse(row.documentCreateDatetime)).toLocaleDateString("en-GB")}</Typography></>)}</TableCell>
                                    {width > 452 ? (<TableCell><Typography><b>Sent: </b> {new Date(Date.parse(row.documentCreateDatetime)).toLocaleDateString("en-GB")}</Typography></TableCell>) : null}
                                    <TableCell>
                                        <IconButton onClick={() => setExpandedRow(expandedRow === index ? null : index)}>
                                            {expandedRow === index ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell align="right"><OpenInNewIcon onClick={onClickUrl(row.documentUrl)} sx={{
                                        color: "primary.dark", "&:hover": {
                                            backgroundColor: "primary.dark",
                                            color: "primary.dark",
                                            borderColor: "primary.dark",
                                        },
                                    }}></OpenInNewIcon></TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ border: "transparent"}}>
                                        <Collapse in={expandedRow === index} timeout="auto" unmountOnExit>
                                            <Paper >
                                                {<iframe src={row.documentUrl} width= "100%" height="600px" title='document-preview'></iframe>}
                                            </Paper>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={filteredData.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => setRowsPerPage(parseInt(e.target.value, 10))}
                    />
                </Container >
                {setShowFooter(true)}</>
            )
    );
}

export default DocumentsPage;